import {
  type Dispatch,
  type FC,
  type PropsWithChildren,
  type SetStateAction,
  createContext,
  useState,
  useContext,
  useEffect,
} from 'react';

interface IdMap {
  userIdJobId: `${string}-${string}`;
  userId: string;
}

interface ProviderState {
  'billable-alert': Record<IdMap['userIdJobId'], 'visible' | 'hidden'>;
  'billable-new-feature-badge': Record<
    IdMap['userIdJobId'],
    'visible' | 'hidden'
  >;
  'expected-salary-alert': Record<IdMap['userId'], 'visible' | 'hidden'>;
  'expected-salary-and-right-to-work-alert': Record<
    IdMap['userId'],
    'visible' | 'hidden'
  >;
  'no-screening-expected-salary-and-right-to-work-alert': Record<
    IdMap['userId'],
    'visible' | 'hidden'
  >;
  'application-card-state': Record<IdMap['userId'], 'expanded' | 'collapsed'>;
  'certsy-verified-full-name-alert': Record<
    IdMap['userId'],
    'visible' | 'hidden'
  >;
}

interface ContextValue {
  'billable-alert': {
    getBillableAlertStatus: () => 'visible' | 'hidden' | undefined;
    hideBillableAlert: () => void;
  };
  'billable-new-feature-badge': {
    getBillableNewFeatureBadgeStatus: () => 'visible' | 'hidden' | undefined;
    hideBillableNewFeatureBadge: () => void;
  };
  'expected-salary-alert': {
    getExpectedSalaryAlert: () => 'visible' | 'hidden' | undefined;
    hideExpectedSalaryAlert: () => void;
  };
  'expected-salary-and-right-to-work-alert': {
    getExpectedSalaryAndRightToWorkAlert: () =>
      | 'visible'
      | 'hidden'
      | undefined;
    hideExpectedSalaryAndRightToWorkAlert: () => void;
  };
  'no-screening-expected-salary-and-right-to-work-alert': {
    getNoScreeningExpectedSalaryAndRightToWorkAlert: () =>
      | 'visible'
      | 'hidden'
      | undefined;
    hideNoScreeningExpectedSalaryAndRightToWorkAlert: () => void;
  };
  'application-card-state': {
    getApplicationCardState: () => 'expanded' | 'collapsed' | undefined;
    toggleApplicationCardState: () => void;
  };
  'certsy-verified-full-name-alert': {
    getCertsyVerifiedFullNameAlert: () => 'visible' | 'hidden' | undefined;
    hideCertsyVerifiedFullNameAlert: () => void;
  };
}

const localStorageKey = 'cm-user-actions';
const createDefaultUserActions = (): ProviderState => ({
  'billable-alert': {},
  'billable-new-feature-badge': {},
  'expected-salary-alert': {},
  'expected-salary-and-right-to-work-alert': {},
  'no-screening-expected-salary-and-right-to-work-alert': {},
  'application-card-state': {},
  'certsy-verified-full-name-alert': {},
});
const syncUserActions = ({
  idMap,
  setValue,
  value,
}: {
  idMap: IdMap;
  value: ProviderState;
  setValue: Dispatch<SetStateAction<ProviderState>>;
}): void => {
  value['billable-alert'] = value['billable-alert'] || {};

  if (!value['billable-alert'][idMap.userIdJobId]) {
    value['billable-alert'][idMap.userIdJobId] = 'visible';
  }

  value['billable-new-feature-badge'] =
    value['billable-new-feature-badge'] || {};

  if (!value['billable-new-feature-badge'][idMap.userIdJobId]) {
    value['billable-new-feature-badge'][idMap.userIdJobId] = 'visible';
  }

  value['expected-salary-and-right-to-work-alert'] =
    value['expected-salary-and-right-to-work-alert'] || {};

  if (!value['expected-salary-and-right-to-work-alert'][idMap.userId]) {
    value['expected-salary-and-right-to-work-alert'][idMap.userId] = 'visible';
  }

  value['expected-salary-alert'] = value['expected-salary-alert'] || {};

  if (!value['expected-salary-alert'][idMap.userId]) {
    value['expected-salary-alert'][idMap.userId] = 'visible';
  }

  value['no-screening-expected-salary-and-right-to-work-alert'] =
    value['no-screening-expected-salary-and-right-to-work-alert'] || {};

  if (
    !value['no-screening-expected-salary-and-right-to-work-alert'][idMap.userId]
  ) {
    value['no-screening-expected-salary-and-right-to-work-alert'][
      idMap.userId
    ] = 'visible';
  }

  value['application-card-state'] = value['application-card-state'] || {};
  if (!value['application-card-state'][idMap.userId]) {
    value['application-card-state'][idMap.userId] = 'expanded';
  }

  value['certsy-verified-full-name-alert'] =
    value['certsy-verified-full-name-alert'] || {};

  if (!value['certsy-verified-full-name-alert'][idMap.userId]) {
    value['certsy-verified-full-name-alert'][idMap.userId] = 'visible';
  }

  setValue(value);
  window.localStorage.setItem(localStorageKey, JSON.stringify(value));
};

const context = createContext<ContextValue | undefined>(undefined);

export const UserActionStatusProvider: FC<
  PropsWithChildren<{ userId: number; jobId: string }>
> = ({ children, userId, jobId }) => {
  const [idMap, setIdMap] = useState<IdMap>();
  const [userActions, setUserActionsState] = useState<ProviderState>(
    createDefaultUserActions(),
  );

  useEffect(() => {
    if (userId && jobId) {
      setIdMap({
        userIdJobId: `${userId}-${jobId}`,
        userId: `${userId}`,
      });
    }
  }, [userId, jobId]);

  useEffect(() => {
    if (!idMap) {
      return;
    }

    const userActionsFromLocalStorage =
      window.localStorage.getItem(localStorageKey);

    if (!userActionsFromLocalStorage) {
      syncUserActions({
        idMap,
        setValue: setUserActionsState,
        value: createDefaultUserActions(),
      });
      return;
    }

    try {
      syncUserActions({
        idMap,
        setValue: setUserActionsState,
        value: JSON.parse(userActionsFromLocalStorage),
      });
    } catch (err) {
      syncUserActions({
        idMap,
        setValue: setUserActionsState,
        value: createDefaultUserActions(),
      });
    }
  }, [idMap]);

  return (
    <context.Provider
      value={{
        'billable-alert': {
          getBillableAlertStatus: () => {
            if (idMap) {
              return userActions['billable-alert'][idMap.userIdJobId];
            }
          },
          hideBillableAlert: () => {
            if (idMap) {
              syncUserActions({
                idMap,
                setValue: setUserActionsState,
                value: {
                  ...userActions,
                  'billable-alert': {
                    ...userActions['billable-alert'],
                    [idMap.userIdJobId]: 'hidden',
                  },
                },
              });
            }
          },
        },
        'billable-new-feature-badge': {
          getBillableNewFeatureBadgeStatus: () => {
            if (idMap) {
              return userActions['billable-new-feature-badge'][
                idMap.userIdJobId
              ];
            }
          },
          hideBillableNewFeatureBadge: () => {
            if (idMap) {
              syncUserActions({
                idMap,
                setValue: setUserActionsState,
                value: {
                  ...userActions,
                  'billable-new-feature-badge': {
                    ...userActions['billable-new-feature-badge'],
                    [idMap.userIdJobId]: 'hidden',
                  },
                },
              });
            }
          },
        },
        'expected-salary-alert': {
          getExpectedSalaryAlert: () => {
            if (idMap) {
              return userActions['expected-salary-alert'][idMap.userId];
            }
          },
          hideExpectedSalaryAlert: () => {
            if (idMap) {
              syncUserActions({
                idMap,
                setValue: setUserActionsState,
                value: {
                  ...userActions,
                  'expected-salary-alert': {
                    ...userActions['expected-salary-alert'],
                    [idMap.userId]: 'hidden',
                  },
                },
              });
            }
          },
        },
        'expected-salary-and-right-to-work-alert': {
          getExpectedSalaryAndRightToWorkAlert: () => {
            if (idMap) {
              return userActions['expected-salary-and-right-to-work-alert'][
                idMap.userId
              ];
            }
          },
          hideExpectedSalaryAndRightToWorkAlert: () => {
            if (idMap) {
              syncUserActions({
                idMap,
                setValue: setUserActionsState,
                value: {
                  ...userActions,
                  'expected-salary-and-right-to-work-alert': {
                    ...userActions['expected-salary-and-right-to-work-alert'],
                    [idMap.userId]: 'hidden',
                  },
                },
              });
            }
          },
        },
        'no-screening-expected-salary-and-right-to-work-alert': {
          getNoScreeningExpectedSalaryAndRightToWorkAlert: () => {
            if (idMap) {
              return userActions[
                'no-screening-expected-salary-and-right-to-work-alert'
              ][idMap.userId];
            }
          },
          hideNoScreeningExpectedSalaryAndRightToWorkAlert: () => {
            if (idMap) {
              syncUserActions({
                idMap,
                setValue: setUserActionsState,
                value: {
                  ...userActions,
                  'no-screening-expected-salary-and-right-to-work-alert': {
                    ...userActions[
                      'no-screening-expected-salary-and-right-to-work-alert'
                    ],
                    [idMap.userId]: 'hidden',
                  },
                },
              });
            }
          },
        },
        'application-card-state': {
          getApplicationCardState: () => {
            if (idMap) {
              return userActions['application-card-state'][idMap.userId];
            }
          },
          toggleApplicationCardState: () => {
            if (idMap) {
              const newCardState =
                userActions['application-card-state'][idMap.userId] ===
                'expanded'
                  ? 'collapsed'
                  : 'expanded';

              syncUserActions({
                idMap,
                setValue: setUserActionsState,
                value: {
                  ...userActions,
                  'application-card-state': {
                    ...userActions['application-card-state'],
                    [idMap.userId]: newCardState,
                  },
                },
              });
            }
          },
        },
        'certsy-verified-full-name-alert': {
          getCertsyVerifiedFullNameAlert: () => {
            if (idMap) {
              return userActions['certsy-verified-full-name-alert'][
                idMap.userId
              ];
            }
          },
          hideCertsyVerifiedFullNameAlert: () => {
            if (idMap) {
              syncUserActions({
                idMap,
                setValue: setUserActionsState,
                value: {
                  ...userActions,
                  'certsy-verified-full-name-alert': {
                    ...userActions['certsy-verified-full-name-alert'],
                    [idMap.userId]: 'hidden',
                  },
                },
              });
            }
          },
        },
      }}
    >
      {children}
    </context.Provider>
  );
};

export const useUserActionStatus = <T extends keyof ContextValue>(
  actionType: T,
): ContextValue[T] => {
  const value = useContext(context);

  if (!value) {
    throw new Error(
      'useUserActionStatus must be used within a UserActionStatusProvider',
    );
  }

  return value[actionType];
};
